(() => {
  if (customElements.get("bento-grid")) return;

  class BentoGrid extends HTMLElement {
    constructor() {
      super();
      this._ro = null;
      this._lastCols = null;
      this._config = null;
      this._templates = new Map();
      this._renderRoot = null;
      this._onResize = null;
    }

    connectedCallback() {
      this._readTemplates();
      this._config = this._readConfig();
      this._ensureObserved();
      this._renderForWidth();
    }

    disconnectedCallback() {
      if (this._ro) this._ro.disconnect();
      this._ro = null;
      if (this._onResize) {
        window.removeEventListener("resize", this._onResize);
        this._onResize = null;
      }
    }

    refresh() {
      this._readTemplates();
      this._config = this._readConfig();
      this._lastCols = null;
      this._renderForWidth();
    }

    _ensureObserved() {
      if (this._ro) return;
      this._ro = new ResizeObserver(() => this._renderForWidth());
      this._ro.observe(this);
      if (this.parentElement) this._ro.observe(this.parentElement);
      if (!this._onResize) {
        this._onResize = () => this._renderForWidth();
        window.addEventListener("resize", this._onResize, { passive: true });
      }
    }

    _readTemplates() {
      this._templates.clear();
      const tpls = this.querySelectorAll("template[data-island]");
      for (const t of tpls) {
        const id = String(t.getAttribute("data-island") || "").trim();
        if (!/^[0-9]$/.test(id)) continue;
        this._templates.set(id, t);
      }
    }

    _readConfig() {
      const script = this.querySelector('script[type="application/json"][data-bento-config]');
      if (script) {
        try {
          return JSON.parse(script.textContent || "{}");
        } catch (e) {
          console.error("[bento-grid] Invalid JSON config.", e);
          return {};
        }
      }
      const raw = this.getAttribute("data-config");
      if (raw) {
        try {
          return JSON.parse(raw);
        } catch (e) {
          console.error("[bento-grid] Invalid data-config JSON.", e);
          return {};
        }
      }
      return {};
    }

    _getRenderRoot() {
      if (!this._renderRoot || !this.contains(this._renderRoot)) {
        this._renderRoot = document.createElement("div");
        this._renderRoot.className = "bento-grid__render-root";
        this.appendChild(this._renderRoot);
      }
      return this._renderRoot;
    }

    _renderForWidth() {
      const cfg = this._config || {};
      const bp3 = (cfg.breakpoints && Number(cfg.breakpoints.three)) || 720;
      const bp4 = (cfg.breakpoints && Number(cfg.breakpoints.four)) || 1100;

      const selfWidth = this.getBoundingClientRect().width || 0;
      const parentWidth = this.parentElement ? this.parentElement.getBoundingClientRect().width : 0;
      const w = selfWidth || parentWidth || window.innerWidth || 0;
      const cols = w >= bp4 ? 4 : w >= bp3 ? 3 : 2;

      if (cols === this._lastCols && this.querySelector(".bento-grid__wrap")) return;
      this._lastCols = cols;
      this._render(cols);
    }

    _render(cols) {
      const cfg = this._config || {};
      const layouts = cfg.layouts || {};
      const layoutRowsRaw = layouts[String(cols)];

      if (!layoutRowsRaw) {
        this._showError(`Missing layouts["${cols}"] (must be array or newline-separated string).`);
        return;
      }

      const matrix = this._parseLayout(layoutRowsRaw, cols);
      if (!matrix.ok) {
        this._showError(matrix.error);
        return;
      }

      const rectCheck = this._validateRectangles(matrix.grid);
      if (!rectCheck.ok) {
        this._showError(rectCheck.error);
        return;
      }

      const rows = matrix.rows;
      const grid = matrix.grid;
      const ids = rectCheck.ids;
      const bboxes = rectCheck.bboxes;

      const columnsCfg = (cfg.columns || {})[String(cols)];
      const colSizes = this._parseSizes(columnsCfg);
      if (colSizes.length !== cols) {
        this._showError(
          `Column widths for ${cols} columns must have exactly ${cols} values (got ${colSizes.length || 0}).`
        );
        return;
      }

      const rowHeight = this._normalizeSize(
        (cfg.rowHeights || {})[String(cols)],
        "100px"
      );
      const rowSizes = Array(rows).fill(rowHeight);

      const gapCfg = (cfg.gaps || {})[String(cols)] || {};
      const gapX = this._normalizeSize(gapCfg.x, "0px");
      const gapY = this._normalizeSize(gapCfg.y, "0px");

      const evenVertical = !!cfg.evenVerticalSpacing;
      const gridHeight = this._normalizeSize(
        (cfg.gridHeights || {})[String(cols)],
        "auto"
      );
      this.style.setProperty("--bento-height", gridHeight);

      const wrap = document.createElement("div");
      wrap.className = "bento-grid__wrap";
      wrap.style.gridTemplateColumns = colSizes.map((v) => this._cssEscape(v)).join(" ");
      wrap.style.gridTemplateRows = rowSizes.map((v) => this._cssEscape(v)).join(" ");
      wrap.style.columnGap = this._cssEscape(gapX);
      wrap.style.rowGap = evenVertical ? "0px" : this._cssEscape(gapY);
      wrap.style.alignContent = evenVertical ? "space-between" : "stretch";

      const items = cfg.items || {};

      for (const id of ids) {
        const bbox = bboxes[id];
        if (!bbox) continue;

        const { minR, maxR, minC, maxC } = bbox;
        const island = document.createElement("div");
        island.className = "bento-grid__item";
        island.dataset.id = id;
        island.style.gridRow = `${minR + 1} / ${maxR + 2}`;
        island.style.gridColumn = `${minC + 1} / ${maxC + 2}`;

        const itemCfg = items[id] || {};
        const autoSize = !!itemCfg.autoSize;
        const sizeCfg = (itemCfg.sizes || {})[String(cols)] || {};
        const itemW = autoSize ? "100%" : this._normalizeSize(sizeCfg.w, "100%");
        const itemH = autoSize ? "100%" : this._normalizeSize(sizeCfg.h, "100%");
        island.style.setProperty("--bento-item-w", itemW);
        island.style.setProperty("--bento-item-h", itemH);

        const gapItem = (itemCfg.gaps || {})[String(cols)] || {};
        const mt = evenVertical ? "0px" : this._normalizeSize(gapItem.top, "0px");
        const mb = evenVertical ? "0px" : this._normalizeSize(gapItem.bottom, "0px");
        const ml = this._normalizeSize(gapItem.left, "0px");
        const mr = this._normalizeSize(gapItem.right, "0px");
        island.style.marginTop = mt;
        island.style.marginBottom = mb;
        island.style.marginLeft = ml;
        island.style.marginRight = mr;

        const tpl = this._templates.get(id) || null;
        if (tpl) island.appendChild(tpl.content.cloneNode(true));

        wrap.appendChild(island);
      }

      const root = this._getRenderRoot();
      root.innerHTML = "";
      root.appendChild(wrap);
    }

    _showError(msg) {
      const root = this._getRenderRoot();
      root.innerHTML = this._errorHTML(msg);
    }

    _parseLayout(rowsRaw, cols) {
      let rawRows = [];
      if (Array.isArray(rowsRaw)) {
        rawRows = rowsRaw;
      } else if (typeof rowsRaw === "string") {
        const trimmed = rowsRaw.trim();
        const unwrapped =
          (trimmed.startsWith("\"") && trimmed.endsWith("\"")) ||
          (trimmed.startsWith("'") && trimmed.endsWith("'"))
            ? trimmed.slice(1, -1)
            : rowsRaw;
        const normalized = unwrapped.replace(/\\r\\n/g, "\n").replace(/\\n/g, "\n").replace(/\\r/g, "\n");
        rawRows = normalized.split(/\r?\n/);
      }

      const cleaned = [];
      for (const row of rawRows) {
        const s = String(row ?? "").replace(/\s+/g, "");
        if (/^["'“”]+$/.test(s)) continue;
        if (s.length === 0) continue;
        cleaned.push(s);
      }
      if (cleaned.length === 0) {
        return { ok: false, error: "Layout is empty after removing whitespace." };
      }

      const rows = cleaned.length;
      for (let i = 0; i < rows; i++) {
        if (cleaned[i].length !== cols) {
          return {
            ok: false,
            error: `Row ${i + 1} must have exactly ${cols} characters (after removing spaces). Got "${cleaned[i]}" (${cleaned[i].length}).`,
          };
        }
        if (!/^[0-9#]+$/.test(cleaned[i])) {
          return {
            ok: false,
            error: `Row ${i + 1} contains invalid characters. Allowed: digits 0-9 and '#'.`,
          };
        }
      }
      const grid = cleaned.map((s) => s.split(""));
      return { ok: true, rows, grid };
    }

    _validateRectangles(grid) {
      const rows = grid.length;
      const cols = grid[0]?.length || 0;
      const idsSet = new Set();

      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const ch = grid[r][c];
          if (ch !== "#") idsSet.add(ch);
        }
      }

      const bboxes = {};
      for (const id of idsSet) {
        let minR = Infinity;
        let minC = Infinity;
        let maxR = -1;
        let maxC = -1;
        for (let r = 0; r < rows; r++) {
          for (let c = 0; c < cols; c++) {
            if (grid[r][c] !== id) continue;
            minR = Math.min(minR, r);
            minC = Math.min(minC, c);
            maxR = Math.max(maxR, r);
            maxC = Math.max(maxC, c);
          }
        }
        if (maxR < 0) continue;
        bboxes[id] = { minR, minC, maxR, maxC };
      }

      for (const id of idsSet) {
        const bbox = bboxes[id];
        if (!bbox) continue;
        for (let r = bbox.minR; r <= bbox.maxR; r++) {
          for (let c = bbox.minC; c <= bbox.maxC; c++) {
            if (grid[r][c] !== id) {
              const found = grid[r][c] ?? "#";
              return {
                ok: false,
                error: `ID ${id} must form a rectangle. Cell (${r + 1},${c + 1}) is '${found}' within its bounding box.`,
              };
            }
          }
        }
      }

      return { ok: true, ids: Array.from(idsSet).sort(), bboxes };
    }

    _parseSizes(raw) {
      if (raw == null) return [];
      const str = String(raw).trim();
      if (!str) return [];
      const parts = str.includes(",") ? str.split(",") : str.split(/\s+/);
      return parts.map((part) => part.trim()).filter(Boolean);
    }

    _normalizeSize(value, fallback) {
      if (value == null) return fallback;
      const str = String(value).trim();
      return str.length ? str : fallback;
    }

    _cssEscape(v) {
      return String(v).replace(/[\n\r;]/g, " ").trim();
    }

    _errorHTML(msg) {
      const safe = String(msg).replace(/</g, "&lt;").replace(/>/g, "&gt;");
      return `
        <div class="bento-grid__error">
          <div class="bento-grid__error-title">bento-grid render error</div>
          <div class="bento-grid__error-msg"><code>${safe}</code></div>
        </div>
      `;
    }
  }

  customElements.define("bento-grid", BentoGrid);
})();
